import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import useDayMessages from "../../hooks/useDayMessages";
import useWindowDimensions from "../../helpers/window";

const STORAGE_KEY = "mindstack_time_indicator_collapsed";

// Time phases and their configurations
const getTimePhase = (hour) => {
  if (hour >= 5 && hour < 7) return "dawn";
  if (hour >= 7 && hour < 12) return "morning";
  if (hour >= 12 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 20) return "evening";
  if (hour >= 20 && hour < 22) return "dusk";
  return "night";
};

const phaseConfig = {
  dawn: {
    greeting: "Good Morning",
    background:
      "linear-gradient(180deg, #2c3e50 0%, #fd746c 50%, #ff9068 100%)",
    orbColor: "#ff6b35",
    orbGlow: "rgba(255, 107, 53, 0.4)",
    skyGradient: ["#1a1a2e", "#fd746c", "#ff9068"],
    orbPosition: 20,
  },
  morning: {
    greeting: "Good Morning",
    background:
      "linear-gradient(180deg, #5b7a9e 0%, #a89b8a 40%, #f0d8a8 70%, #e8c895 100%)",
    orbColor: "#f5c96e",
    orbGlow: "rgba(245, 201, 110, 0.4)",
    skyGradient: ["#5b7a9e", "#a89b8a", "#f0d8a8"],
    orbPosition: 40,
  },
  afternoon: {
    greeting: "Good Afternoon",
    background:
      "linear-gradient(180deg, #2193b0 0%, #6dd5ed 50%, #87ceeb 100%)",
    orbColor: "#f9d71c",
    orbGlow: "rgba(249, 215, 28, 0.6)",
    skyGradient: ["#2193b0", "#6dd5ed", "#87ceeb"],
    orbPosition: 70,
  },
  evening: {
    greeting: "Good Evening",
    background:
      "linear-gradient(180deg, #e67e22 0%, #d35400 30%, #c0392b 70%, #8b3a3a 100%)",
    orbColor: "#ff8c42",
    orbGlow: "rgba(255, 140, 66, 0.5)",
    skyGradient: ["#e67e22", "#d35400", "#c0392b"],
    orbPosition: 40,
  },
  dusk: {
    greeting: "Good Evening",
    background:
      "linear-gradient(180deg, #d35400 0%, #c0392b 40%, #8b3a3a 70%, #5c2e2e 100%)",
    orbColor: "#ffa500",
    orbGlow: "rgba(255, 165, 0, 0.4)",
    skyGradient: ["#d35400", "#c0392b", "#8b3a3a"],
    orbPosition: 20,
  },
  night: {
    greeting: "Good Night",
    background:
      "linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)",
    orbColor: "#e8e8e8",
    orbGlow: "rgba(232, 232, 232, 0.3)",
    skyGradient: ["#0a0a0a", "#1a1a2e", "#16213e"],
    orbPosition: 50,
  },
};

// Beautiful morphing SVG for sun/moon
const CelestialOrb = ({ phase, config }) => {
  const isSun = ["dawn", "morning", "afternoon", "evening"].includes(phase);

  return (
    <svg
      viewBox="0 0 100 100"
      className="time-indicator__orb-svg"
      style={{
        filter: `drop-shadow(0 0 20px ${config.orbGlow})`,
      }}
    >
      <defs>
        {/* Animated gradient for the sun */}
        {isSun && (
          <radialGradient id={`sunAnimatedGradient-${phase}`} cx="50%" cy="50%">
            <stop offset="0%" stopColor="#fffbf0">
              <animate
                attributeName="stop-color"
                values="#fffbf0;#fff8e1;#ffecb3;#ffe082;#ffecb3;#fff8e1;#fffbf0"
                dur="8s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="50%" stopColor={config.orbColor}>
              <animate
                attributeName="stop-color"
                values={`${config.orbColor};#ffd54f;#ffca28;#ffc107;#ffca28;#ffd54f;${config.orbColor}`}
                dur="8s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="100%" stopColor={config.orbColor}>
              <animate
                attributeName="stop-color"
                values={`${config.orbColor};#ffca28;#ffc300;#ffcc33;#ffc300;#ffca28;${config.orbColor}`}
                dur="8s"
                repeatCount="indefinite"
              />
            </stop>
          </radialGradient>
        )}

        {/* Gradient for the moon */}
        {!isSun && (
          <radialGradient id={`orbGradient-${phase}`} cx="30%" cy="30%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="50%" stopColor={config.orbColor} />
            <stop offset="100%" stopColor="#c4c4c4" />
          </radialGradient>
        )}

        {/* Glass effect gradient */}
        <linearGradient
          id={`glassEffect-${phase}`}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="rgba(255,255,255,0.6)" />
          <stop offset="50%" stopColor="rgba(255,255,255,0.1)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>

        {/* Outer glow filter */}
        <filter
          id={`glow-${phase}`}
          x="-50%"
          y="-50%"
          width="200%"
          height="200%"
        >
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Main orb with glass morphism */}
      <circle
        cx="50"
        cy="50"
        r="25"
        fill={
          isSun
            ? `url(#sunAnimatedGradient-${phase})`
            : `url(#orbGradient-${phase})`
        }
        filter={`url(#glow-${phase})`}
        className="time-indicator__orb-main"
      />

      {/* Glass highlight overlay */}
      <ellipse
        cx="42"
        cy="42"
        rx="12"
        ry="8"
        fill={`url(#glassEffect-${phase})`}
        opacity="0.8"
      />

      {/* Moon craters (only for night) */}
      {phase === "night" && (
        <g className="time-indicator__craters">
          <circle cx="42" cy="55" r="4" fill="rgba(180,180,180,0.3)" />
          <circle cx="58" cy="45" r="3" fill="rgba(180,180,180,0.2)" />
        </g>
      )}

      {/* Stars (only for night/dusk) - around the orb */}
      {(phase === "night" || phase === "dusk") && (
        <g className="time-indicator__stars">
          {[
            { cx: 15, cy: 20, r: 1 },
            { cx: 85, cy: 15, r: 1.5 },
            { cx: 80, cy: 75, r: 1 },
            { cx: 20, cy: 80, r: 1.2 },
            { cx: 10, cy: 50, r: 0.8 },
            { cx: 90, cy: 45, r: 1 },
            { cx: 45, cy: 12, r: 0.7 },
            { cx: 75, cy: 35, r: 0.8 },
            { cx: 30, cy: 70, r: 0.7 },
            { cx: 5, cy: 35, r: 0.9 },
            { cx: 50, cy: 25, r: 0.75 },
            { cx: 92, cy: 72, r: 0.85 },
            { cx: 55, cy: 88, r: 0.8 },
            { cx: 5, cy: 15, r: 0.8 },
            { cx: 42, cy: 78, r: 0.7 },
          ].map((star, i) => (
            <circle
              key={i}
              cx={star.cx}
              cy={star.cy}
              r={star.r}
              fill="#fff"
              opacity="0.8"
              style={{
                animation: `starTwinkle 2s ease-in-out ${i * 0.3}s infinite`,
              }}
            />
          ))}
        </g>
      )}
    </svg>
  );
};

const TimeIndicator = ({
  contextVariables = {},
  completedToday = 0,
  totalTargets = 0,
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const { width } = useWindowDimensions();
  const isSmallFont = width < 1500; // font-size 12px for collapsed indicators
  const isVerticalLayout = width < 680 || (width >= 768 && width < 1150); // stack time/progress/message vertically
  const isVerySmall = width < 600; // keep time text small at tiny screens

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const hour = currentTime.getHours();
  const minute = currentTime.getMinutes();
  const phase = getTimePhase(hour);
  const config = phaseConfig[phase];

  // Calculate percentage through the day (0-100%)
  const dayProgress = ((hour * 60 + minute) / (24 * 60)) * 100;

  // Get contextual message based on time and task state
  const contextualMessage = useDayMessages(phase, contextVariables);

  const timeString = currentTime.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const dateString = currentTime.toLocaleDateString([], {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <Box
      className={`time-indicator ${isCollapsed ? "time-indicator--collapsed" : ""}`}
      sx={{
        background: config.background,
        borderRadius: "24px",
        padding: isCollapsed
          ? "12px 20px"
          : isVerticalLayout
            ? "14px 16px"
            : "32px 24px",
        position: "relative",
        overflow: "hidden",
        backdropFilter: "blur(10px)",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.35)",
        cursor: "pointer",
        transition: "padding 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      onClick={toggleCollapsed}
    >
      {/* Glass morphism overlay */}
      <Box
        className="time-indicator__glass-overlay"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* Expand/Collapse toggle */}
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          toggleCollapsed();
        }}
        sx={{
          position: "absolute",
          top: isCollapsed ? "50%" : 8,
          right: 8,
          transform: isCollapsed ? "translateY(-50%)" : "none",
          color: "rgba(255,255,255,0.7)",
          zIndex: 3,
          padding: "4px",
          transition:
            "top 0.35s cubic-bezier(0.4, 0, 0.2, 1), transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            color: "#fff",
            background: "rgba(255,255,255,0.15)",
          },
        }}
      >
        {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
      </IconButton>

      {/* === COLLAPSED VIEW === */}
      <Box
        className="time-indicator__collapsed-content"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          position: isCollapsed ? "relative" : "absolute",
          opacity: isCollapsed ? 1 : 0,
          height: isCollapsed ? "auto" : 0,
          overflow: "hidden",
          transition: "opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          zIndex: 1,
          pointerEvents: isCollapsed ? "auto" : "none",
        }}
      >
        {/* Time on the left */}
        <Typography
          variant="h5"
          sx={{
            color: "#fff",
            fontWeight: 700,
            fontSize: isSmallFont
              ? isVerySmall
                ? "0.85rem"
                : "1rem"
              : "1.4rem",
            textShadow: "0 2px 4px rgba(0,0,0,0.3)",
            letterSpacing: "1px",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {timeString}
        </Typography>

        {/* Day progress bar + targets bar in the middle */}
        <Box
          sx={{
            width: isSmallFont ? "160px" : "280px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          {/* Day progress bar with % text inside */}
          <Box
            sx={{
              height: "18px",
              background: "rgba(0, 0, 0, 0.3)",
              borderRadius: "9px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${dayProgress}%`,
                background:
                  "linear-gradient(90deg, #197b68 0%, #276580 50%, #6a205c 100%)",
                transition: "width 1s ease",
                boxShadow: "0 0 8px rgba(25, 123, 104, 0.5)",
                borderRadius: "9px",
                minWidth: "40px",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "#FFFFFF",
                fontSize: isSmallFont ? "0.6rem" : "0.65rem",
                fontWeight: 700,
                textShadow: "0 1px 3px rgba(0,0,0,0.6)",
                whiteSpace: "nowrap",
                pointerEvents: "none",
              }}
            >
              {Math.round(100 - dayProgress)}% of day left
            </Typography>
          </Box>

          {/* Targets progress bar with icon + text inside */}
          {totalTargets > 0 && (
            <Box
              sx={{
                height: "18px",
                background: "rgba(0, 0, 0, 0.3)",
                borderRadius: "9px",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${Math.min((completedToday / totalTargets) * 100, 100)}%`,
                  background:
                    "linear-gradient(90deg, #2e7d32 0%, #43a047 100%)",
                  transition: "width 1s ease",
                  boxShadow: "0 0 8px rgba(46, 125, 50, 0.5)",
                  borderRadius: "9px",
                  minWidth: completedToday > 0 ? "40px" : "0",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  display: "flex",
                  alignItems: "center",
                  gap: "3px",
                  pointerEvents: "none",
                }}
              >
                <CheckCircleOutlineIcon
                  sx={{ fontSize: "0.7rem", color: "#FFFFFF" }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "#FFFFFF",
                    fontSize: isSmallFont ? "0.6rem" : "0.65rem",
                    fontWeight: 700,
                    textShadow: "0 1px 3px rgba(0,0,0,0.6)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {completedToday}/{totalTargets}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Sun/Moon orb on the right */}
        <Box
          sx={{
            width: "48px",
            height: "48px",
            flexShrink: 0,
          }}
        >
          <CelestialOrb phase={phase} config={config} />
        </Box>
      </Box>

      {/* === EXPANDED VIEW === */}
      <Box
        className="time-indicator__expanded-content"
        sx={{
          opacity: isCollapsed ? 0 : 1,
          maxHeight: isCollapsed ? 0 : "500px",
          overflow: "hidden",
          transition:
            "opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1), max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          pointerEvents: isCollapsed ? "none" : "auto",
        }}
      >
        <Box
          className="time-indicator__content"
          sx={
            isVerticalLayout
              ? {
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  position: "relative",
                  zIndex: 1,
                }
              : {}
          }
        >
          <Box
            className="time-indicator__text-section"
            sx={{
              background: "rgba(0, 0, 0, 0.15)",
              padding: isVerticalLayout ? "8px 14px" : "16px 20px",
              borderRadius: "16px",
              backdropFilter: "blur(8px)",
              display: "inline-block",
              maxWidth: "fit-content",
              ...(isVerticalLayout
                ? { order: 1, alignSelf: "flex-start" }
                : {}),
            }}
          >
            <Typography
              variant="h4"
              className="time-indicator__greeting"
              sx={{
                color: "#fff",
                fontWeight: 600,
                textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                marginBottom: isVerticalLayout ? "4px" : "8px",
                fontSize: isVerticalLayout ? "1rem" : undefined,
              }}
            >
              {config.greeting}
            </Typography>
            <Typography
              variant="h2"
              className="time-indicator__time"
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontSize: isVerySmall
                  ? "1.6rem"
                  : isVerticalLayout
                    ? "2rem"
                    : "3rem",
                textShadow: "0 2px 8px rgba(0,0,0,0.4)",
                letterSpacing: "2px",
              }}
            >
              {timeString}
            </Typography>
            <Typography
              variant="body1"
              className="time-indicator__date"
              sx={{
                color: "rgba(255,255,255,0.95)",
                marginTop: isVerticalLayout ? "4px" : "8px",
                textShadow: "0 1px 3px rgba(0,0,0,0.3)",
                fontSize: isVerySmall
                  ? "0.75rem !important"
                  : isVerticalLayout
                    ? "1rem !important"
                    : "0.75rem !important",
              }}
            >
              {dateString}
            </Typography>
          </Box>

          {/* Contextual Message */}
          <Box
            className="time-indicator__message"
            sx={
              isVerticalLayout
                ? {
                    position: "relative",
                    left: "unset",
                    top: "unset",
                    transform: "none",
                    textAlign: "center",
                    maxWidth: "100%",
                    width: "100%",
                    zIndex: 2,
                    order: 3,
                  }
                : {
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    maxWidth: "75%",
                    zIndex: 2,
                  }
            }
          >
            <Typography
              variant="body1"
              sx={{
                color: "#fff",
                fontWeight: 500,
                fontSize: isVerticalLayout ? "0.75rem !important" : "0.95rem !important",
                textShadow: "0 2px 8px rgba(0,0,0,0.5)",
                lineHeight: 1.4,
                background: "rgba(0, 0, 0, 0.2)",
                padding: isVerticalLayout ? "7px 14px" : "12px 20px",
                borderRadius: "12px",
                backdropFilter: "blur(8px)",
                marginBottom: isVerticalLayout ? "8px" : "16px",
              }}
            >
              {contextualMessage}
            </Typography>

            {/* Day Progress Bar + Targets Bar */}
            <Box
              className="time-indicator__progress-container"
              sx={{
                background: "rgba(0, 0, 0, 0.2)",
                padding: isVerticalLayout ? "7px 12px" : "12px 16px",
                borderRadius: "12px",
                backdropFilter: "blur(8px)",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              {/* Day progress bar with % text inside */}
              <Box
                sx={{
                  height: "22px",
                  background: "rgba(0, 0, 0, 0.3)",
                  borderRadius: "11px",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${dayProgress}%`,
                    background:
                      "linear-gradient(90deg, #197b68 0%, #276580 50%, #6a205c 100%)",
                    transition: "width 1s ease",
                    boxShadow: "0 0 8px rgba(25, 123, 104, 0.5)",
                    borderRadius: "11px",
                    minWidth: "40px",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    color: "#FFFFFF",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    textShadow: "0 1px 3px rgba(0,0,0,0.6)",
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                  }}
                >
                  {Math.round(100 - dayProgress)}% of day left
                </Typography>
              </Box>

              {/* Targets bar with icon + text inside */}
              {totalTargets > 0 && (
                <Box
                  sx={{
                    height: "22px",
                    background: "rgba(0, 0, 0, 0.3)",
                    borderRadius: "11px",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${Math.min((completedToday / totalTargets) * 100, 100)}%`,
                      background:
                        "linear-gradient(90deg, #2e7d32 0%, #43a047 100%)",
                      transition: "width 1s ease",
                      boxShadow: "0 0 8px rgba(46, 125, 50, 0.5)",
                      borderRadius: "11px",
                      minWidth: completedToday > 0 ? "40px" : "0",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      pointerEvents: "none",
                    }}
                  >
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: "0.8rem", color: "#FFFFFF" }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#FFFFFF",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        textShadow: "0 1px 3px rgba(0,0,0,0.6)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {completedToday} / {totalTargets} targets completed
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Box
            className="time-indicator__orb-container"
            sx={{
              width: isVerticalLayout ? "72px" : "160px",
              height: isVerticalLayout ? "72px" : "160px",
              position: "relative",
              ...(isVerticalLayout ? { order: 2 } : {}),
            }}
          >
            <CelestialOrb phase={phase} config={config} />
          </Box>
        </Box>
      </Box>

      {/* Floating clouds */}
      <Box className="time-indicator__clouds">
        {[...Array(9)].map((_, i) => {
          const cloudVariants = [
            "M15 35C15 35 8 35 8 28C8 21 15 21 15 21C15 21 15 14 22 14C29 14 29 21 29 21C29 21 36 14 43 14C50 14 50 21 50 21C50 21 57 14 64 14C71 14 71 21 71 21C71 21 78 21 78 28C78 35 71 35 71 35H15Z",
            "M10 30C10 30 5 30 5 25C5 20 10 20 10 20C10 20 10 15 15 15C20 15 20 20 20 20C20 20 25 15 35 15C45 15 45 20 45 20C45 20 55 15 65 15C75 15 75 20 75 20C75 20 85 15 90 15C95 15 95 20 95 20C95 20 100 20 100 25C100 30 95 30 95 30H10Z",
            "M20 30C20 30 15 30 15 25C15 20 20 20 20 20C20 20 25 15 30 15C35 15 40 20 40 20C40 20 45 15 50 15C55 15 60 20 60 20C60 20 65 20 65 25C65 30 60 30 60 30H20Z",
          ];

          const variant = cloudVariants[i % 3];
          // Scale clouds by half when collapsed
          const baseScale = isCollapsed ? 1.5 : 3;
          const scale = baseScale + (i % 3) * (isCollapsed ? 0.25 : 0.5);
          const width = (60 + (i % 4) * 20) * scale;
          const height = (35 + (i % 3) * 10) * scale;
          // Adjust vertical positioning for collapsed view - keep clouds within visible area
          const top = isCollapsed ? 10 + (i % 3) * 20 : 10 + (i % 5) * 15;
          const duration = 20 + (i % 5) * 5;
          // Use negative delay so clouds start mid-animation (already in motion),
          // preventing them from visibly spawning in from the left edge.
          const delay = -((i / 9) * duration);

          return (
            <Box
              key={i}
              sx={{
                position: "absolute",
                opacity: 0.5,
                top: `${top}%`,
                // Start fully off-screen left using the cloud's actual pixel width
                left: `-${width}px`,
                animation: `floatCloud ${duration}s linear infinite`,
                animationDelay: `${delay}s`,
                // Pass width as a CSS variable so the keyframe can travel 100vw + cloud width
                "--cloud-width": `${width}px`,
              }}
            >
              <svg
                width={width}
                height={height}
                viewBox="0 0 100 40"
                fill="none"
              >
                <path d={variant} fill="white" opacity="0.85" />
              </svg>
            </Box>
          );
        })}
      </Box>

      {/* Animated background particles */}
      <Box className="time-indicator__particles">
        {[...Array(6)].map((_, i) => {
          // Adjust particle positioning for collapsed view
          const particleTop = isCollapsed
            ? 20 + (i % 2) * 30
            : 20 + (i % 3) * 25;
          const particleSize = isCollapsed ? "3px" : "4px";

          return (
            <Box
              key={i}
              className="time-indicator__particle"
              sx={{
                position: "absolute",
                width: particleSize,
                height: particleSize,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.3)",
                left: `${15 + i * 15}%`,
                top: `${particleTop}%`,
                animation: `floatParticle ${3 + i * 0.5}s ease-in-out infinite`,
                animationDelay: `${i * 0.2}s`,
              }}
            />
          );
        })}
      </Box>

      {/* Twinkling stars across the sky (night/dusk only) */}
      {(phase === "night" || phase === "dusk") && (
        <Box
          className="time-indicator__sky-stars"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          {[
            { left: "3%", top: "8%", size: 2, delay: 0 },
            { left: "8%", top: "22%", size: 1.5, delay: 0.4 },
            { left: "12%", top: "55%", size: 2, delay: 1.2 },
            { left: "6%", top: "78%", size: 1.5, delay: 2.1 },
            { left: "15%", top: "12%", size: 2.5, delay: 0.8 },
            { left: "18%", top: "38%", size: 1.5, delay: 1.6 },
            { left: "14%", top: "88%", size: 2, delay: 3.0 },
            { left: "22%", top: "5%", size: 2, delay: 0.3 },
            { left: "25%", top: "30%", size: 1.5, delay: 2.5 },
            { left: "28%", top: "65%", size: 2, delay: 1.0 },
            { left: "24%", top: "85%", size: 1.5, delay: 3.4 },
            { left: "32%", top: "15%", size: 2.5, delay: 0.6 },
            { left: "35%", top: "48%", size: 1.5, delay: 2.8 },
            { left: "38%", top: "72%", size: 2, delay: 1.4 },
            { left: "33%", top: "92%", size: 1.5, delay: 3.8 },
            { left: "42%", top: "8%", size: 2, delay: 0.9 },
            { left: "45%", top: "25%", size: 1.5, delay: 2.2 },
            { left: "48%", top: "58%", size: 2, delay: 1.7 },
            { left: "44%", top: "82%", size: 2.5, delay: 3.2 },
            { left: "52%", top: "12%", size: 1.5, delay: 0.5 },
            { left: "55%", top: "35%", size: 2, delay: 2.0 },
            { left: "58%", top: "68%", size: 1.5, delay: 1.3 },
            { left: "53%", top: "90%", size: 2, delay: 3.6 },
            { left: "62%", top: "6%", size: 2, delay: 0.7 },
            { left: "65%", top: "42%", size: 2.5, delay: 2.4 },
            { left: "68%", top: "75%", size: 1.5, delay: 1.1 },
            { left: "63%", top: "55%", size: 2, delay: 3.0 },
            { left: "72%", top: "10%", size: 1.5, delay: 0.2 },
            { left: "75%", top: "32%", size: 2, delay: 2.6 },
            { left: "78%", top: "62%", size: 1.5, delay: 1.8 },
            { left: "73%", top: "88%", size: 2, delay: 3.5 },
            { left: "82%", top: "18%", size: 2.5, delay: 0.4 },
            { left: "85%", top: "45%", size: 1.5, delay: 2.3 },
            { left: "88%", top: "78%", size: 2, delay: 1.5 },
            { left: "83%", top: "92%", size: 1.5, delay: 3.9 },
            { left: "92%", top: "8%", size: 2, delay: 0.1 },
            { left: "95%", top: "28%", size: 1.5, delay: 2.7 },
            { left: "90%", top: "52%", size: 2, delay: 1.9 },
            { left: "96%", top: "70%", size: 2.5, delay: 3.3 },
            { left: "93%", top: "85%", size: 1.5, delay: 0.6 },
          ]
            .filter((star) => {
              // When collapsed, only show stars in the top 60% of the component
              if (isCollapsed) {
                const topPercent = parseFloat(star.top);
                return topPercent <= 60;
              }
              return true;
            })
            .map((star, i) => {
              // Scale stars down when collapsed
              const starSize = isCollapsed ? star.size * 0.7 : star.size;
              return (
                <Box
                  key={i}
                  sx={{
                    position: "absolute",
                    left: star.left,
                    top: star.top,
                    width: `${starSize}px`,
                    height: `${starSize}px`,
                    borderRadius: "50%",
                    background: "#fff",
                    opacity: 0.7,
                    animation: `starTwinkle 2.5s ease-in-out ${star.delay}s infinite`,
                  }}
                />
              );
            })}
        </Box>
      )}
    </Box>
  );
};

export default TimeIndicator;
